import React, { useState, useEffect } from 'react';
import { FiUsers, FiCalendar, FiAward, FiMessageSquare, FiRefreshCw } from 'react-icons/fi';
import { BsPersonLinesFill } from 'react-icons/bs';
import { Bar, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Link } from 'react-router-dom';
import api from '../../libs/axios';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

// TypeScript interfaces matching the actual API response
interface DashboardStats {
    totalUsers: number;
    totalMatches: number;
    completedMatches: number;
    totalTournaments: number;
    completedTournaments: number;
    communityPosts: number;
    activeUsers: number;
}

interface RecentMatch {
    id: number;
    teamA: string;
    teamB: string;
    date: string;
    status: string;
    winner?: string;
}

interface RecentUser {
    id: number;
    name: string;
    email: string;
    role: string;
    joined: string;
}

interface PlayerPerformance {
    player: string;
    runs: number;
    wickets: number;
    matches: number;
    playerId: number;
}

interface ChartData {
    matchesChartData: {
        labels: string[];
        datasets: Array<{
            label: string;
            data: number[];
            backgroundColor: string;
        }>;
    };
    playerRoleData: {
        labels: string[];
        datasets: Array<{
            data: number[];
            backgroundColor: string[];
        }>;
    };
}

interface DashboardData {
    stats: DashboardStats;
    recentMatches: RecentMatch[];
    recentUsers: RecentUser[];
    playerPerformance: PlayerPerformance[];
    chartData: ChartData;
}

const Dashboard: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch dashboard data
    const fetchDashboardData = async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/dashboard');

            setDashboardData(response.data);
        } catch (err) {
            console.error('Dashboard API Error:', err);
            setError('Failed to load dashboard data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchDashboardData();
    }, []);

    // Loading state
    if (loading) {
        return (
            <div className="p-4 md:p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="flex items-center space-x-2">
                        <FiRefreshCw className="animate-spin text-primary-500 text-xl" />
                        <span className="text-gray-600">Loading dashboard data...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="p-4 md:p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <div className="text-red-600 mr-3">⚠️</div>
                            <div>
                                <h3 className="text-red-800 font-medium">Error Loading Dashboard</h3>
                                <p className="text-red-600 text-sm mt-1">{error}</p>
                            </div>
                        </div>
                        <button
                            onClick={fetchDashboardData}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // No data state
    if (!dashboardData) {
        return (
            <div className="p-4 md:p-6">
                <div className="text-center text-gray-600">
                    No dashboard data available.
                </div>
            </div>
        );
    }

    const { stats, recentMatches, recentUsers, playerPerformance, chartData } = dashboardData;

    return (
        <div className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-primary-950 flex items-center">
                    <span className="bg-primary-700 w-1 h-6 mr-3 rounded"></span>
                    Dashboard
                </h2>
                <button
                    onClick={fetchDashboardData}
                    className="flex items-center border px-3 py-1.5 rounded-full hover:border-primary-500 focus:outline-none space-x-2 text-primary-600 hover:text-primary-700 text-sm"
                >
                    <FiRefreshCw className="text-sm" />
                    <span>Refresh</span>
                </button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                    icon={<FiUsers className="text-primary-500 text-2xl" />}
                    title="Total Users"
                    value={stats.totalUsers}
                    change={`${stats.activeUsers} Active users`}
                />
                <StatCard
                    icon={<FiCalendar className="text-green-500 text-2xl" />}
                    title="Total Matches"
                    value={stats.totalMatches}
                    change={`${stats.completedMatches} Completed`}
                />
                <StatCard
                    icon={<FiAward className="text-purple-500 text-2xl" />}
                    title="Total Tournaments"
                    value={stats.totalTournaments}
                    change={`${stats.completedTournaments} Completed`}
                />
                <StatCard
                    icon={<FiMessageSquare className="text-yellow-500 text-2xl" />}
                    title="Community Posts"
                    value={stats.communityPosts}
                    change="Total posts"
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 space-y-6 lg:space-y-0 lg:gap-6 mb-8">
                <div className="bg-white p-4 md:p-6 rounded-lg shadow col-span-2">
                    <h3 className="font-semibold text-lg mb-4">Matches Played (Last 6 Months)</h3>
                    <div className="h-64">
                        <Bar
                            data={chartData.matchesChartData}
                            options={{ responsive: true, maintainAspectRatio: false }}
                        />
                    </div>
                </div>
                <div className="bg-white p-4 md:p-6 rounded-lg shadow">
                    <h3 className="font-semibold text-lg mb-4">Player Roles Distribution</h3>
                    <div className="h-64">
                        <Pie
                            data={chartData.playerRoleData}
                            options={{ responsive: true, maintainAspectRatio: false }}
                        />
                    </div>
                </div>
            </div>

            {/* Recent Matches and Users */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white p-4 md:p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-lg">Recent Matches</h3>
                        <Link to="/matches" className="text-primary-500 text-sm font-medium hover:text-primary-600">
                            View All
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {recentMatches.length > 0 ? (
                            recentMatches.map(match => (
                                <MatchCard 
                                    key={match.id} 
                                    match={match}
                                />
                            ))
                        ) : (
                            <div className="text-gray-500 text-center py-4">
                                No recent matches found
                            </div>
                        )}
                    </div>
                </div>
                <div className="bg-white p-4 md:p-6 rounded-lg shadow">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-lg">Recent Users</h3>
                        <Link to="/users" className="text-primary-500 text-sm font-medium hover:text-primary-600">
                            View All
                        </Link>
                    </div>
                    <div className="space-y-4">
                        {recentUsers.length > 0 ? (
                            recentUsers.map(user => (
                                <UserCard key={user.id} user={user} />
                            ))
                        ) : (
                            <div className="text-gray-500 text-center py-4">
                                No recent users found
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Player Performance */}
            <div className="bg-white p-4 md:p-6 rounded-lg shadow mb-8">
                <h3 className="font-semibold text-lg mb-4">Top Player Performance</h3>
                {playerPerformance.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Runs</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Wickets</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Matches</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {playerPerformance.map((player, index) => (
                                    <tr key={`${player.playerId}-${index}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                                                    <BsPersonLinesFill className="text-primary-500" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{player.player}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{player.runs}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{player.wickets}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{player.matches}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                                ${player.runs > 100 || player.wickets > 5 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {player.runs > 100 || player.wickets > 5 ? 'Excellent' : 'Good'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-gray-500 text-center py-8">
                        No player performance data available
                    </div>
                )}
            </div>
        </div>
    );
};

// Component for Stat Cards
const StatCard: React.FC<{
    icon: React.ReactNode;
    title: string;
    value: number;
    change: string;
}> = ({ icon, title, value, change }) => {
    return (
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-500 text-sm font-medium">{title}</p>
                    <h3 className="text-2xl font-bold mt-1">{value?.toLocaleString()}</h3>
                    <p className="text-gray-400 text-xs mt-2">{change}</p>
                </div>
                <div className="bg-gray-100 p-3 rounded-full">
                    {icon}
                </div>
            </div>
        </div>
    );
};

// Component for Match Cards - showing actual status from DB
const MatchCard: React.FC<{ 
    match: RecentMatch;
}> = ({ match }) => {
    
    const getStatusBadge = () => {
        const status = match.status.toLowerCase();
        
        if (status === 'completed') {
            return (
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full capitalize">
                    {match.winner ? `${match.winner} won` : 'Completed'}
                </span>
            );
        } else if (status === 'live') {
            return (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full capitalize">
                    {match.status}
                </span>
            );
        } else {
            return (
                <span className="bg-gray-100 text-primary-700 text-xs px-2 py-1 rounded-full capitalize">
                    {match.status}
                </span>
            );
        }
    };

    return (
        <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition">
            <div className="flex items-center space-x-4">
                <div className="bg-gray-100 p-2 rounded-lg">
                    <FiCalendar className="text-gray-500" />
                </div>
                <div>
                    <h4 className="font-medium text-gray-900">{match.teamA} vs {match.teamB}</h4>
                    <p className="text-sm text-gray-500">{new Date(match.date).toLocaleDateString()}</p>
                </div>
            </div>
            <div>
                {getStatusBadge()}
            </div>
        </div>
    );
};

// Component for User Cards
const UserCard: React.FC<{ user: RecentUser }> = ({ user }) => {
    const getRoleBadgeClass = (role: string): string => {
        switch (role.toLowerCase()) {
            case 'admin':
                return 'bg-purple-100 text-purple-800';
            case 'umpire':
                return 'bg-blue-100 text-blue-800';
            case 'owner':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-green-100 text-green-800';
        }
    };

    return (
        <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition">
            <div className="flex items-center space-x-4">
                <div className="bg-gray-100 p-2 rounded-full">
                    <FiUsers className="text-gray-500" />
                </div>
                <div>
                    <h4 className="font-medium text-gray-900">{user.name}</h4>
                    <p className="text-sm text-gray-500">{user.email}</p>
                </div>
            </div>
            <div>
                <span className={`text-xs px-2 py-1 capitalize rounded-full ${getRoleBadgeClass(user.role)}`}>
                    {user.role}
                </span>
            </div>
        </div>
    );
};

export default Dashboard;